import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { X, Mail, Briefcase } from "lucide-react";
import api from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Chip-based email recipient input with contact autocomplete.
 *
 * As the user types, we hit `/api/contacts/search?q=…` and show a list of
 * matching addresses — teammates first, then most-active learned contacts.
 *
 * Props:
 *   value: string[]
 *   onChange(next: string[])
 *   placeholder
 *   testIdPrefix
 */
export default function EmailChipsInput({ value, onChange, placeholder, testIdPrefix = "email-chips" }) {
  const [draft, setDraft] = useState("");
  const [error, setError] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [popRect, setPopRect] = useState(null); // {left, top, width} for the portalled dropdown
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const blurTimerRef = useRef(null);

  // Fetch suggestions whenever the draft changes (debounced 120ms).
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts/search", { params: { q: draft, limit: 8 } });
        // Filter out anyone already chipped + obvious junk
        const filtered = (data || []).filter((c) => c.email && !value.includes(c.email));
        setSuggestions(filtered);
        setHighlight(0);
      } catch {
        setSuggestions([]);
      }
    }, 120);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [draft, open, value]);

  // Track the wrapper's viewport rect so the portalled dropdown sticks to it.
  // Re-measures on scroll/resize so the popup follows the input if the dialog
  // scrolls, the window is resized, etc.
  useLayoutEffect(() => {
    if (!open) { setPopRect(null); return; }
    const update = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const desired = 288; // matches max-h-72
      // Flip above the input when there's noticeably more room above.
      const openAbove = spaceBelow < 180 && spaceAbove > spaceBelow;
      const top = openAbove
        ? Math.max(8, r.top - Math.min(desired, spaceAbove - 8) - 4)
        : r.bottom + 4;
      setPopRect({ left: r.left, top, width: r.width, maxHeight: openAbove ? Math.min(desired, spaceAbove - 8) : Math.min(desired, spaceBelow - 8) });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, suggestions.length, value.length]);

  const commit = (raw) => {
    const trimmed = (raw || "").trim().replace(/[,;]+$/, "");
    if (!trimmed) return true;
    if (!EMAIL_RE.test(trimmed)) {
      setError(true);
      setTimeout(() => setError(false), 600);
      return false;
    }
    if (value.includes(trimmed)) {
      setDraft("");
      return true;
    }
    onChange([...value, trimmed]);
    setDraft("");
    setSuggestions([]);
    return true;
  };

  const pickSuggestion = (c) => {
    if (!c || !c.email) return;
    if (!value.includes(c.email)) onChange([...value, c.email]);
    setDraft("");
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const onKeyDown = (e) => {
    if (open && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        // Pick the highlighted suggestion only if the draft is non-empty;
        // an empty draft + Enter shouldn't accidentally add anyone.
        if (draft.trim()) {
          e.preventDefault();
          pickSuggestion(suggestions[highlight]);
          return;
        }
      }
    }
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === " ") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && !draft && value.length > 0) {
      onChange(value.slice(0, -1));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const onChangeText = (e) => {
    const v = e.target.value;
    setOpen(true);
    if (/[,;]\s*$/.test(v)) {
      commit(v.replace(/[,;]\s*$/, ""));
    } else {
      setDraft(v);
    }
  };

  const onPaste = (e) => {
    const text = e.clipboardData.getData("text") || "";
    if (/[,;\s]/.test(text)) {
      e.preventDefault();
      const parts = text.split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
      const fresh = [];
      let anyInvalid = false;
      for (const p of parts) {
        if (!EMAIL_RE.test(p)) { anyInvalid = true; continue; }
        if (value.includes(p) || fresh.includes(p)) continue;
        fresh.push(p);
      }
      if (fresh.length) onChange([...value, ...fresh]);
      if (anyInvalid) {
        setError(true);
        setTimeout(() => setError(false), 600);
      }
    }
  };

  const removeAt = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
    inputRef.current?.focus();
  };

  const onFocus = () => {
    setOpen(true);
  };

  const onBlur = () => {
    // Delay so a mousedown on a suggestion has time to register before we
    // close the dropdown.
    blurTimerRef.current = setTimeout(() => {
      commit(draft);
      setOpen(false);
    }, 150);
  };

  return (
    <div className="relative">
      <div
        ref={wrapperRef}
        className={`flex flex-wrap gap-1.5 items-center min-h-[42px] rounded-md border bg-white px-2 py-1.5 transition-all
          ${error ? "border-red-400 ring-2 ring-red-200 animate-pulse" : "border-zinc-300 focus-within:border-[#0033CC] focus-within:ring-2 focus-within:ring-[#0033CC]/20"}`}
        onClick={() => inputRef.current?.focus()}
        data-testid={`${testIdPrefix}-wrapper`}
      >
        {value.map((email, i) => (
          <span
            key={`${email}-${i}`}
            className="inline-flex items-center gap-1 max-w-full bg-[#0033CC]/10 text-[#0033CC] border border-[#0033CC]/20 rounded-full pl-2 pr-1 py-0.5 text-xs font-medium"
            data-testid={`${testIdPrefix}-chip-${i}`}
          >
            <Mail className="w-3 h-3 shrink-0" strokeWidth={2} />
            <span className="truncate max-w-[200px] md:max-w-[260px]">{email}</span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeAt(i); }}
              className="ml-0.5 p-0.5 hover:bg-white rounded-full text-[#0033CC] hover:text-red-600"
              title="Remove"
              data-testid={`${testIdPrefix}-chip-remove-${i}`}
            >
              <X className="w-3 h-3" strokeWidth={2.5} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="email"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          value={draft}
          onChange={onChangeText}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={value.length ? "" : (placeholder || "name@example.com")}
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm py-1"
          data-testid={`${testIdPrefix}-input`}
        />
      </div>

      {open && suggestions.length > 0 && popRect && createPortal(
        <div
          className="fixed z-[100] overflow-y-auto bg-white border border-zinc-200 rounded-md shadow-lg"
          style={{ left: popRect.left, top: popRect.top, width: popRect.width, maxHeight: popRect.maxHeight }}
          data-testid={`${testIdPrefix}-suggestions`}
          onMouseDown={(e) => {
            // Cancel the input's blur-driven dropdown close while interacting.
            if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
            e.preventDefault();
          }}
        >
          {suggestions.map((c, i) => (
            <button
              key={c.email}
              type="button"
              onClick={() => pickSuggestion(c)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${i === highlight ? "bg-[#0033CC]/10" : "hover:bg-zinc-50"}`}
              data-testid={`${testIdPrefix}-suggestion-${i}`}
            >
              {c.is_employee ? (
                <Briefcase className="w-3.5 h-3.5 text-[#0033CC] shrink-0" strokeWidth={2} />
              ) : (
                <Mail className="w-3.5 h-3.5 text-zinc-400 shrink-0" strokeWidth={1.75} />
              )}
              <div className="flex flex-col min-w-0 flex-1">
                {c.name ? (
                  <>
                    <span className="text-sm font-medium text-zinc-900 truncate">{c.name}</span>
                    <span className="text-xs text-zinc-500 truncate font-mono">{c.email}</span>
                  </>
                ) : (
                  <span className="text-sm text-zinc-900 truncate font-mono">{c.email}</span>
                )}
              </div>
              {c.is_employee && (
                <span className="text-[9px] font-semibold tracking-wide uppercase text-[#0033CC] bg-[#0033CC]/10 px-1.5 py-0.5 rounded shrink-0">Team</span>
              )}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}
